import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMuiModal } from '../../../_core/hooks';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { Theme } from '@mui/material';

export type useOpenConfirmModalFormHookProps = {
	title: string;
	description?: string;
	icon?: ReactNode;
	danger?: boolean;
	info?: boolean;
	submitButtonName?: string;
	toastText?: string;
	customBackgrondColorKey?: keyof Theme['colors'];
	customColorKey?: keyof Theme['colors'];
	customBorderColorKey?: keyof Theme['colors'];
	featchAction(values?: any): Promise<void>;
	handlePagination?(): void;
	additionalWork?(): void;
	modalWidth?: number;
};

export function useOpenConfirmModal(props: useOpenConfirmModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		additionalWork,
		title,
		description,
		icon,
		danger = false,
		info = false,
		submitButtonName,
		toastText,
		customBackgrondColorKey,
		customBorderColorKey,
		customColorKey,
		...modalProps
	} = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const { openModal, closeModal } = useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleConfirm = async (value: any): Promise<void> => {
		try {
			await featchAction(value);
			showToast(toastText ?? 'Successfully performed', 'success');
			handlePagination && handlePagination();
			closeModal();
			additionalWork && additionalWork();
		} catch (error) {
			console.error(error);
			showToast('Something goes wrong', 'error');
			closeModal();
		}
	};

	const handleOpenConfirmModal = useCallback(
		(value: any) => {
			const handleSubmit = async () => {
				await handleConfirm(value);
			};
			openModal({
				title,
				description: description?.replace(':familyName', value.fullName),
				onSubmit: handleSubmit,
				onClose: closeModal,
				icon,
				customBackgrondColorKey: customBackgrondColorKey ? customBackgrondColorKey : 'white',
				customColorKey: customBackgrondColorKey ? customBackgrondColorKey : danger ? 'red' : 'blue',
				customBorderColorKey: customBorderColorKey ? customBorderColorKey : danger ? 'red' : undefined,
				danger,
				leftButtonProps: {
					children: t('families.forms.buttons.cancel'),
					variant: 'secondary',
					sx: buttonModalWindowStyles,
				},
				rightButtonProps: {
					children: submitButtonName,
					variant: danger? 'danger': info? 'info' : 'primary',
					contraModeStyleButton: true,
					sx: buttonModalWindowStyles,
				},
				...modalProps
			});
		},
		[handleConfirm, openModal]
	);

	return handleOpenConfirmModal;
}
